import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { waLink, IMAGES } from "@/lib/site";
import { EASE } from "./Reveal";

const LINES = ["INTERNET", "DE FIBRA", "EM BERTIOGA."];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, 160]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen flex-col overflow-hidden pb-20 pt-28"
    >
      <motion.div
        style={{ y: glowY }}
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 left-1/2 h-[620px] w-[900px] max-w-[140vw] -translate-x-1/2"
      >
        <div className="h-full w-full rounded-full bg-violet-600/20 blur-[140px]" />
      </motion.div>

      <div className="mx-auto grid w-full max-w-7xl flex-1 items-center gap-14 px-5 sm:px-8 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <h1 className="font-display font-black leading-[0.95] tracking-tight">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className="block text-[clamp(2.55rem,9.5vw,7rem)]"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.25 + i * 0.13, ease: EASE }}
                >
                  {i === LINES.length - 1 ? (
                    <span className="text-violet-400">{line}</span>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75, ease: EASE }}
            className="mt-7 max-w-xl text-base leading-relaxed text-white/60 sm:text-lg"
          >
            Planos a partir de R$ 79,90/mês pra casa, trabalho, streaming e
            gamers. Chama no WhatsApp e instala sem complicação.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              data-testid="hero-cta-whatsapp"
              href={waLink("Olá! Quero assinar um plano da HPNET.")}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-md bg-violet-600 px-7 py-4 text-sm font-medium text-white shadow-[0_0_40px_rgba(124,58,237,0.45)] transition-colors duration-200 hover:bg-violet-500"
            >
              Contratar pelo WhatsApp
            </a>
            <a
              data-testid="hero-cta-planos"
              href="#planos"
              className="flex items-center gap-2 rounded-md border border-white/10 bg-white/5 px-7 py-4 text-sm font-medium text-white transition-colors duration-200 hover:bg-white/10"
            >
              Ver planos
              <ArrowDown className="h-4 w-4" />
            </a>
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 48 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.7, ease: EASE }}
          >
            <motion.div
              style={{ y: imgY }}
              className="relative overflow-hidden rounded-3xl border border-white/10 shadow-[0_40px_120px_-30px_rgba(124,58,237,0.45)]"
            >
              <img
                src={IMAGES.hero}
                alt="Gamers jogando em um setup com iluminação neon"
                className="h-[320px] w-full object-cover sm:h-[420px]"
              />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
